import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { PhoneCallButton } from "@/components/ui/phone-call-button";
import { Save } from "lucide-react";
import { OrganizationCustomFieldsForm } from "@/components/deals/OrganizationCustomFieldsForm";

export interface CompanyFormData {
  name: string;
  cnpj: string;
  industry: string;
  size: string;
  website: string;
  phone: string;
  email: string;
  instagram: string;
  location: string;
  description: string;
  status: string;
  custom_data?: Record<string, any>;
}

interface CompanyFormProps {
  defaultValues?: Partial<CompanyFormData>;
  onSubmit: (data: CompanyFormData) => void;
  onCancel?: () => void;
  submitLabel?: string;
  isLoading?: boolean;
}

export const CompanyForm = ({ 
  defaultValues, 
  onSubmit, 
  onCancel, 
  submitLabel = "Salvar Empresa",
  isLoading = false 
}: CompanyFormProps) => {
  const [formData, setFormData] = useState<CompanyFormData>({
    name: "",
    cnpj: "",
    industry: "",
    size: "",
    website: "",
    phone: "",
    email: "",
    instagram: "",
    location: "",
    description: "",
    status: "active",
    custom_data: {},
    ...defaultValues
  });

  const handleInputChange = (field: keyof CompanyFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCustomDataChange = (customData: Record<string, any>) => {
    setFormData(prev => ({
      ...prev,
      custom_data: customData
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('🔍 [CompanyForm] Submitting formData:', formData);
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Nome da Empresa */}
        <div className="space-y-2">
          <Label htmlFor="name">Nome da Empresa *</Label>
          <Input
            id="name"
            value={formData.name}
            onChange={(e) => handleInputChange("name", e.target.value)}
            placeholder="Digite o nome da empresa"
            required
          />
        </div>

        {/* CNPJ */}
        <div className="space-y-2">
          <Label htmlFor="cnpj">CNPJ</Label>
          <Input
            id="cnpj"
            value={formData.cnpj}
            onChange={(e) => handleInputChange("cnpj", e.target.value)}
            placeholder="00.000.000/0000-00"
          />
        </div>

        {/* Segmento */}
        <div className="space-y-2">
          <Label htmlFor="industry">Segmento</Label>
          <SearchableSelect
            value={formData.industry}
            onValueChange={(value) => handleInputChange("industry", value)}
            placeholder="Selecione o segmento"
            options={[
              { value: "tecnologia", label: "Tecnologia" },
              { value: "saude", label: "Saúde" },
              { value: "educacao", label: "Educação" },
              { value: "financeiro", label: "Financeiro" },
              { value: "varejo", label: "Varejo" },
              { value: "manufatura", label: "Manufatura" },
              { value: "servicos", label: "Serviços" },
              { value: "consultoria", label: "Consultoria" },
              { value: "outros", label: "Outros" },
            ]}
          />
        </div>

        {/* Tamanho */}
        <div className="space-y-2">
          <Label htmlFor="size">Tamanho da Empresa</Label>
          <SearchableSelect
            value={formData.size}
            onValueChange={(value) => handleInputChange("size", value)}
            placeholder="Selecione o tamanho"
            options={[
              { value: "micro", label: "Startup (1-10 funcionários)" },
              { value: "small", label: "Pequena (11-50 funcionários)" },
              { value: "medium", label: "Média (51-200 funcionários)" },
              { value: "large", label: "Grande (201-1000 funcionários)" },
              { value: "enterprise", label: "Corporação (1000+ funcionários)" },
            ]}
          />
        </div>

        {/* Status */}
        <div className="space-y-2">
          <Label htmlFor="status">Status</Label>
          <SearchableSelect
            value={formData.status}
            onValueChange={(value) => handleInputChange("status", value)}
            placeholder="Selecione o status"
            options={[
              { value: "prospect", label: "Prospect" },
              { value: "active", label: "Ativa" },
              { value: "customer", label: "Cliente" },
              { value: "churned", label: "Inativa" },
            ]}
          />
        </div>

        {/* Website */}
        <div className="space-y-2">
          <Label htmlFor="website">Website</Label>
          <Input
            id="website"
            type="url"
            value={formData.website}
            onChange={(e) => handleInputChange("website", e.target.value)}
            placeholder="https://exemplo.com.br"
          />
        </div>

        {/* Telefone */}
        <div className="space-y-2">
          <Label htmlFor="phone">Telefone</Label>
          <div className="relative flex items-center">
            <Input
              id="phone"
              value={formData.phone}
              onChange={(e) => handleInputChange("phone", e.target.value)}
              placeholder="(11) 99999-9999"
              className="pr-12"
            />
            <div className="absolute right-2 top-1/2 -translate-y-1/2">
              <PhoneCallButton 
                phoneNumber={formData.phone || ""} 
                size="sm"
                variant="ghost"
              />
            </div>
          </div>
        </div>

        {/* Email */}
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange("email", e.target.value)}
            placeholder="contato@empresa.com.br"
          />
        </div>

        {/* Instagram */}
        <div className="space-y-2">
          <Label htmlFor="instagram">Instagram</Label>
          <Input
            id="instagram"
            value={formData.instagram}
            onChange={(e) => handleInputChange("instagram", e.target.value)}
            placeholder="@empresa"
          />
        </div>

        {/* Localização */}
        <div className="space-y-2">
          <Label htmlFor="location">Localização</Label>
          <Input
            id="location"
            value={formData.location}
            onChange={(e) => handleInputChange("location", e.target.value)}
            placeholder="São Paulo, SP"
          />
        </div>
      </div>

      {/* Descrição */}
      <div className="space-y-2">
        <Label htmlFor="description">Descrição</Label>
        <Textarea
          id="description"
          value={formData.description}
          onChange={(e) => handleInputChange("description", e.target.value)}
          placeholder="Descreva a empresa, seus principais produtos ou serviços..."
          rows={4}
        />
      </div>

      {/* Seção de Campos Personalizados Organizacionais */}
      <div className="space-y-4 pt-4 border-t">
        <div>
          <h3 className="text-lg font-semibold">Informações Adicionais</h3>
          <p className="text-sm text-muted-foreground">Campos personalizados configurados para empresas</p>
        </div>
        <OrganizationCustomFieldsForm 
          customData={formData.custom_data} 
          pageType="companies" 
          onCustomDataChange={handleCustomDataChange}
        />
      </div>

      {/* Actions */}
      <div className="flex justify-end space-x-4 pt-6">
        {onCancel && (
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
          >
            Cancelar
          </Button>
        )}
        <Button type="submit" disabled={isLoading}>
          <Save className="h-4 w-4 mr-2" />
          {isLoading ? "Salvando..." : submitLabel}
        </Button>
      </div>
    </form>
  );
};